import { useCallback } from "react";

import { useTranslations } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import { PRIZE_TYPE_SUFFIX } from "./constants";

export type UsePrizeCtaInput = {
  prizePoints: number;
  balance: number;
  ctaLabel: string;
  earnPointsCtaText?: string;
  earnPointsCtaUrl?: string;
  onClick: () => void;
  type?: string;
  redeemedTimes?: number;
  redeemLimit?: number;
  isLotteryExpired: boolean;
  intl: Intl.NumberFormat;
};

export type PrizeCta = {
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
};

export const usePrizeCta = () => {
  const { __ } = useTranslations();

  const getPrizeCta = useCallback(
    ({
      prizePoints,
      balance,
      ctaLabel,
      earnPointsCtaText,
      earnPointsCtaUrl,
      onClick,
      type,
      redeemedTimes,
      redeemLimit,
      isLotteryExpired,
      intl,
    }: UsePrizeCtaInput): PrizeCta => {
      const pointsDeficit = Math.max(prizePoints - balance, 0);
      const isNotEnoughPoints = pointsDeficit > 0;
      const isAlreadyRedeemed = (redeemedTimes ?? 0) >= (redeemLimit && redeemLimit >= 0 ? redeemLimit : Infinity);

      // at this point we have affixes for those types *_physical || *_simplified but same actions are applied
      const isDelayedLottery = type?.startsWith(PRIZE_TYPE_SUFFIX.delayed);
      const isInstantLottery = type?.startsWith(PRIZE_TYPE_SUFFIX.instant);
      const isLottery = isDelayedLottery || isInstantLottery;

      const earnMorePointsLabel = replaceStringPlaceholder(
        earnPointsCtaText ?? "",
        [{ placeholder: "missingPoints", value: intl.format(pointsDeficit) }],
        false
      );

      if (isDelayedLottery && isLotteryExpired) {
        return { label: __("prizes.contest.ended.label"), disabled: true };
      }

      if (isLottery && isAlreadyRedeemed) {
        return { label: __("prizes.already.participated.label"), disabled: true };
      }

      if (isNotEnoughPoints) {
        return { label: earnMorePointsLabel, href: earnPointsCtaUrl };
      }

      if (isDelayedLottery) {
        return { label: __("prizes.play.delayed.lottery.label"), onClick };
      }

      if (isInstantLottery) {
        return { label: __("prizes.play.instant.lottery.label"), onClick };
      }

      return { label: ctaLabel, onClick };
    },
    [__]
  );

  return getPrizeCta;
};
